import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * US Market timezone constants
 */
export const US_TIMEZONE = "America/New_York"; // EST/EDT

/**
 * Get user's local timezone from browser
 * Automatically detects the user's system timezone
 */
export function getUserTimezone(): string {
  return dayjs.tz.guess();
}

/**
 * Parse US market time string (e.g., "09:30-16:00") and convert to user's local time
 * Handles DST automatically using dayjs timezone plugin
 *
 * @param timeRange - Time range in US Eastern Time (e.g., "09:30-16:00")
 * @param date - Date in YYYY-MM-DD format (for determining DST)
 * @param t - i18n translate function
 * @returns Object with US and user's local time ranges
 *
 * @example
 * convertUSToUserTime("09:30-16:00", "2025-11-28", t)
 * // Returns (for user in Beijing):
 * // {
 * //   us: { start: "09:30", end: "16:00", timezone: "EST" },
 * //   user: { start: "22:30", end: "05:00 (次日)", timezone: "Asia/Shanghai (UTC+8)", nextDay: true }
 * // }
 */
export function convertUSToUserTime(
  timeRange: string,
  date: string,
  t: (key: string) => string,
): {
  us: { start: string; end: string; timezone: string };
  user: { start: string; end: string; timezone: string; nextDay: boolean };
} {
  // Get user's timezone
  const userTimezone = getUserTimezone();

  // Empty time range means market is closed
  if (!timeRange) {
    return {
      us: { start: t("trading.market-closed"), end: t("trading.market-closed"), timezone: "EST/EDT" },
      user: { start: t("trading.market-closed"), end: t("trading.market-closed"), timezone: `${userTimezone}`, nextDay: false },
    };
  }

  const [startTime, endTime] = timeRange.split("-");
  if (!startTime || !endTime) {
    return {
      us: { start: t("trading.market-closed"), end: t("trading.market-closed"), timezone: "EST/EDT" },
      user: { start: t("trading.market-closed"), end: t("trading.market-closed"), timezone: `${userTimezone}`, nextDay: false },
    };
  }

  // Parse date and time in US timezone
  const usStart = dayjs.tz(`${date} ${startTime}`, US_TIMEZONE);
  const usEnd = dayjs.tz(`${date} ${endTime}`, US_TIMEZONE);

  // Get timezone abbreviation (EST vs EDT) using offsetName API
  const timezoneAbbr = usStart.offsetName("short") || "EST/EDT";

  // Convert to user's local time
  const userStart = usStart.tz(userTimezone);
  const userEnd = usEnd.tz(userTimezone);

  // Get user's timezone offset (e.g., UTC+8, UTC-5)
  const userOffset = userStart.format("Z");
  const userTimezoneLabel = `${userTimezone} (UTC${userOffset})`;

  // Check if end time is on next day
  const nextDay = userEnd.date() !== userStart.date();
  const nextDayLabel = t("trading.next-day");
  const userEndStr = nextDay ? `${userEnd.format("HH:mm")} ${nextDayLabel}` : userEnd.format("HH:mm");

  return {
    us: {
      start: startTime,
      end: endTime,
      timezone: timezoneAbbr,
    },
    user: {
      start: userStart.format("HH:mm"),
      end: userEndStr,
      timezone: userTimezoneLabel,
      nextDay,
    },
  };
}

/**
 * Format holiday trading hours for display in tooltip
 *
 * @param holiday - Holiday info object
 * @param t - i18n translate function
 * @returns Formatted string with US and user's local times
 *
 * @example
 * formatHolidayTradingHours({
 *   name: "Thanksgiving Day",
 *   tradingHour: "09:30-13:00",
 *   postMarket: "13:00-17:00"
 * }, t)
 * // Returns (for user in Beijing with zh-CN locale):
 * // "🇺🇸 交易时段: 09:30-13:00 (EST)
 //  📍 您的时区: 22:30-02:00 (次日) (Asia/Shanghai UTC+8)
 //  📈 盘后交易: 13:00-17:00 (EST)
 //  📍 您的时区: 02:00-06:00 (次日) (Asia/Shanghai UTC+8)"
 */
export function formatHolidayTradingHours(
  holiday: {
    name: string;
    tradingHour: string;
    postMarket: string;
    date: string;
  },
  t: (key: string) => string,
): string {
  const lines: string[] = [];

  // Regular trading hours
  if (holiday.tradingHour) {
    const regular = convertUSToUserTime(holiday.tradingHour, holiday.date, t as any);
    lines.push(`🇺🇸 ${t("trading.trading-hours")}: ${regular.us.start}-${regular.us.end} (${regular.us.timezone})`);
    lines.push(`📍 ${t("trading.your-time")}: ${regular.user.start}-${regular.user.end} (${regular.user.timezone})`);
  }

  // Post-market hours
  if (holiday.postMarket) {
    const post = convertUSToUserTime(holiday.postMarket, holiday.date, t);
    lines.push(`📈 ${t("trading.post-market")}: ${post.us.start}-${post.us.end} (${post.us.timezone})`);
    lines.push(`📍 ${t("trading.your-time")}: ${post.user.start}-${post.user.end} (${post.user.timezone})`);
  }

  // Market closed
  if (!holiday.tradingHour && !holiday.postMarket) {
    lines.push(`🔴 ${t("trading.market-closed")}`);
  }

  return lines.join("\n");
}

/**
 * Get holiday type for UI styling
 *
 * @param holiday - Holiday info object
 * @returns Holiday type: 'full-closed' | 'early-close' | 'post-only' | 'normal'
 */
export function getHolidayType(holiday: {
  tradingHour: string;
  postMarket: string;
}): "full-closed" | "early-close" | "post-only" | "normal" {
  if (!holiday.tradingHour && !holiday.postMarket) {
    return "full-closed"; // 全天休市
  }
  if (holiday.tradingHour === "09:30-16:00" && holiday.postMarket === "16:00-17:00") {
    return "normal"; // 正常交易
  }
  if (!holiday.tradingHour) {
    return "post-only"; // 只有盘后
  }
  return "early-close"; // 提前闭市
}
